import type { HttpCore } from '../http';
import type {
  CreateDeviceRequest,
  Device,
  DeviceReport,
  DeviceSummary,
  Lookback,
  OkResponse,
  Performance,
  RevalidateDeviceRequest,
  UpdateDeviceRequest,
  ValidateDeviceRequest,
  ValidateDeviceResponse,
} from '../types';

/** `/api/v1/devices/**` — inverter connections owned by the signed-in customer. */
export function createDevicesApi(http: HttpCore) {
  return {
    /** The API wraps the list: `{ items: Device[] }`. */
    list: () => http.get<{ items: Device[] }>('/devices').then((r) => r.items ?? []),
    get: (id: string) => http.get<Device>(`/devices/${encodeURIComponent(id)}`),
    /** Checks credentials and lists the sites they can see. Nothing is stored. */
    validate: (body: ValidateDeviceRequest) => http.post<ValidateDeviceResponse>('/devices/validate', body),
    create: (body: CreateDeviceRequest) => http.post<Device>('/devices', body),
    update: (id: string, body: UpdateDeviceRequest) =>
      http.patch<Device>(`/devices/${encodeURIComponent(id)}`, body),
    remove: (id: string) => http.del<OkResponse>(`/devices/${encodeURIComponent(id)}`),
    /** Re-checks the stored credentials, or replaces them first when `body.credentials` is given. */
    revalidate: (id: string, body?: RevalidateDeviceRequest) =>
      http.post<Device>(`/devices/${encodeURIComponent(id)}/revalidate`, body),
    summary: (id: string) => http.get<DeviceSummary>(`/devices/${encodeURIComponent(id)}/summary`),
    /** Actual production against what the weather should have given (gap T1). */
    performance: (id: string, days = 30) =>
      http.get<Performance>(`/devices/${encodeURIComponent(id)}/performance?days=${days}`),
    /** Every stored day read once: days at zero, days with no record, days far below the days around them. Free. */
    lookback: (id: string) => http.get<Lookback>(`/devices/${encodeURIComponent(id)}/lookback`),
    /** Every month this inverter has a report for, newest first. The API wraps the list. */
    reports: (id: string) =>
      http.get<{ items: DeviceReport[] }>(`/devices/${encodeURIComponent(id)}/reports`).then((r) => r.items ?? []),
    /**
     * The month's report as a PDF, re-rendered from its snapshot. A plain link cannot fetch it — the
     * endpoint needs the Bearer token — so it comes back as a blob, like the invoice.
     */
    reportPdf: (id: string, month: string) =>
      http.blob(`/devices/${encodeURIComponent(id)}/reports/${encodeURIComponent(month)}/pdf`),
  };
}

export type DevicesApi = ReturnType<typeof createDevicesApi>;
